// The landing page's three numbers (copy/product.js STATS), with a gentle
// count-up the first time they scroll into view. With reduced motion asked
// for, or no IntersectionObserver, they show their final values at once. A
// screen reader hears the final value only, never the numbers rolling past.
import React, { useEffect, useRef, useState } from 'react';
import { STATS } from '../copy/product';

const still = () => typeof window === 'undefined'
  || typeof window.IntersectionObserver !== 'function'
  || Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

// 'still': the final value; 'waiting': 0 until seen; 'run': 0 up to the value.
function useCountUp(to, phase, ms = 1100) {
  const [n, setN] = useState(phase === 'still' ? to : 0);
  useEffect(() => {
    if (phase === 'still') { setN(to); return undefined; }
    if (phase !== 'run') return undefined;
    let frame = null;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / ms);
      setN(Math.round(to * (1 - (1 - p) ** 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, phase, ms]);
  return n;
}

function Stat({ value, suffix, label, phase }) {
  const n = useCountUp(value, phase);
  return (
    <li className="lp-stat">
      <span className="lp-stat-num" aria-hidden="true">{n.toLocaleString('en-US')}{suffix}</span>
      <span className="lp-sr">{value.toLocaleString('en-US')}{suffix} </span>
      <span className="lp-stat-label">{label}</span>
    </li>
  );
}

export default function LandingStats() {
  const ref = useRef(null);
  const [phase, setPhase] = useState(() => (still() ? 'still' : 'waiting'));
  useEffect(() => {
    if (phase !== 'waiting' || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setPhase('run'); io.disconnect(); }
    }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [phase]);
  return (
    <section className="lp-stats" aria-label="In numbers">
      <ul className="lp-stats-row" ref={ref}>
        {STATS.map((s) => (
          <Stat key={s.label} {...s} phase={phase} />
        ))}
      </ul>
    </section>
  );
}
